import React, { useState } from 'react';
import type { SecurityFormData, FormErrors } from '../types';
import { VALIDATION_MESSAGES } from '../constants';

interface SecurityCardProps {
  isEditing: boolean;
  onEdit: () => void;
  onSave: (currentPassword: string, newPassword: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const SecurityCard: React.FC<SecurityCardProps> = ({
  isEditing,
  onEdit,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<SecurityFormData>({
    currentPassword: '',
    newPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const validatePassword = (password: string): boolean => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = VALIDATION_MESSAGES.REQUIRED_OLD_PASSWORD;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = VALIDATION_MESSAGES.REQUIRED_NEW_PASSWORD;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = VALIDATION_MESSAGES.PASSWORD_MIN_LENGTH;
    } else if (!validatePassword(formData.newPassword)) {
      newErrors.newPassword = VALIDATION_MESSAGES.PASSWORD_REQUIREMENTS;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        await onSave(formData.currentPassword, formData.newPassword);
        setFormData({ currentPassword: '', newPassword: '' });
      } catch (error) {
        console.error('Save error:', error);
      }
    }
  };

  const handleCancel = () => {
    setFormData({ currentPassword: '', newPassword: '' });
    setErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    onCancel();
  };

  if (!isEditing) {
    return (
      <div
        className="bg-white border border-[#EDEDED] rounded-2xl"
        style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
                fill="#0B111E"
              />
            </svg>
            <h3
              style={{
                fontFamily: 'Manrope',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '25px',
                color: '#0B111E',
              }}
            >
              Security
            </h3>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <span
              style={{
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '22px',
                color: '#0B111E',
              }}
            >
              Password
            </span>
            <span
              style={{
                fontFamily: 'Manrope',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '19px',
                color: '#757575',
              }}
            >
              ••••••••••••••••
            </span>
          </div>
          <button
            onClick={onEdit}
            className="px-4 py-2 border border-[#0B111E] rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Manrope',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#0B111E',
            }}
          >
            Reset Password
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#EDEDED] rounded-2xl"
      style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}
    >
      <div className="flex items-center gap-1">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"
            fill="#0B111E"
          />
        </svg>
        <h3
          style={{
            fontFamily: 'Manrope',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '25px',
            color: '#0B111E',
          }}
        >
          Security
        </h3>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 relative">
          <input
            type={showCurrentPassword ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
            className="w-full px-4 py-3 pr-12 border border-[#A5A5A5] rounded-lg focus:outline-none focus:border-[#015FA6] focus:ring-1 focus:ring-[#015FA6]"
            style={{
              fontFamily: 'Manrope',
              fontSize: '14px',
              lineHeight: '19px',
              color: '#0B111E',
            }}
            disabled={loading}
            autoComplete="off"
            placeholder="Enter your current password"
          />
          <label
            className="absolute left-3 -top-3 px-2 bg-white"
            style={{
              fontFamily: 'Manrope',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#0B111E',
            }}
          >
            Current Password<span className="text-[#DC0000]">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z"
                fill="#0B111E"
              />
            </svg>
          </button>
          {errors.currentPassword && (
            <span className="text-[#DC0000] text-xs mt-1">{errors.currentPassword}</span>
          )}
        </div>

        <div className="flex-1 relative">
          <input
            type={showNewPassword ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            placeholder="Enter your new password"
            className="w-full px-4 py-3 pr-12 border border-[#A5A5A5] rounded-lg focus:outline-none focus:border-[#015FA6] focus:ring-1 focus:ring-[#015FA6]"
            style={{
              fontFamily: 'Manrope',
              fontSize: '14px',
              lineHeight: '19px',
              color: '#0B111E',
            }}
            disabled={loading}
            autoComplete="new-password"
          />
          <label
            className="absolute left-3 -top-3 px-2 bg-white rounded-full"
            style={{
              fontFamily: 'Manrope',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#0B111E',
            }}
          >
            New Password<span className="text-[#DC0000]">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z"
                fill="#0B111E"
              />
            </svg>
          </button>
          {errors.newPassword && (
            <span className="text-[#DC0000] text-xs mt-1">{errors.newPassword}</span>
          )}
        </div>
      </div>

      <div className="flex gap-3.5">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors disabled:opacity-50"
          style={{
            fontFamily: 'Manrope',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          disabled={loading}
          className="px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          style={{
            fontFamily: 'Manrope',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            color: '#515151',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SecurityCard;
